import React, { useEffect, useMemo, useState } from 'react';
import { Button, Collapse, Divider, Input, Layout, Segmented, Space, Tag, Typography } from 'antd';
import ReactPlayer from 'react-player';
import { ArrowLeft, CheckCircle2, LogIn, RotateCcw, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import FooterBar from '../Layout/FooterBar';
import aksLogo from '../assets/images/aks_wings_logo_dark.png';
import { useAppSelector } from '../hooks';
import {
  advanceGuidedFlow,
  getGuidedFlowNode,
  goBackGuidedFlow,
  isGuidedFlowResultNode,
} from './FAQFlow';
import type { GuidedFlow } from './FAQFlow';
import type { HelpArticle, HelpTopic } from './FAQContent';
import { createFallbackHelpCenterState, subscribeToPublishedHelpTopics } from './FAQContentSource';
import type { HelpCenterContentState } from './FAQContentSource';
import { searchHelpTopics } from './FAQSearch';
import type { HelpSearchResult, HelpSearchResultKind } from './FAQSearch';
import PublicFAQContent from './ContentBank/PublicFAQContent';
import { subscribeToPublishedContentBankDocuments } from './ContentBank/contentBankService';
import type { ContentBankDocument } from './ContentBank/types';

const { Content, Header } = Layout;
const { Title, Paragraph, Text } = Typography;

type FAQView = 'documents' | 'app';

const pageStyles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: '#f5f7f8',
  },
  header: {
    height: 'auto',
    minHeight: 72,
    lineHeight: 'normal',
    padding: '12px 24px',
    background: '#fff',
    borderBottom: '1px solid #dce3e7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minWidth: 0,
    color: 'inherit',
    textDecoration: 'none',
  },
  logo: {
    width: 42,
    height: 42,
    objectFit: 'contain',
  },
  content: {
    maxWidth: 1120,
    margin: '0 auto',
    padding: '40px 24px 56px',
    width: '100%',
  },
  contentWide: {
    maxWidth: 1520,
    padding: '28px 32px 56px',
  },
  hero: {
    display: 'grid',
    gap: 18,
    marginBottom: 28,
  },
  search: {
    maxWidth: 640,
  },
  topics: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 12,
    marginBottom: 32,
  },
  topicButton: {
    width: '100%',
    minHeight: 116,
    font: 'inherit',
    lineHeight: 1.4,
    border: '1px solid #d6dee3',
    background: '#fff',
    borderRadius: 8,
    padding: 16,
    textAlign: 'left',
    cursor: 'pointer',
  },
  topicButtonActive: {
    borderColor: '#0f766e',
    boxShadow: '0 0 0 2px rgba(15, 118, 110, 0.12)',
  },
  topicPanel: {
    background: '#fff',
    border: '1px solid #dce3e7',
    borderRadius: 8,
    padding: 24,
  },
  flowPanel: {
    border: '1px solid #dce3e7',
    borderRadius: 8,
    padding: 20,
    background: '#fbfcfc',
    marginBottom: 24,
  },
  answerButton: {
    height: 'auto',
    minHeight: 64,
    padding: '12px 14px',
    whiteSpace: 'normal',
    textAlign: 'left',
    display: 'block',
  },
  answerLabel: {
    display: 'block',
    fontWeight: 600,
    whiteSpace: 'normal',
  },
  answerDescription: {
    display: 'block',
    marginTop: 4,
    color: '#64748b',
    whiteSpace: 'normal',
  },
  resultList: {
    paddingLeft: 0,
    listStyle: 'none',
    margin: '16px 0',
    display: 'grid',
    gap: 10,
  },
  searchResults: {
    background: '#fff',
    border: '1px solid #dce3e7',
    borderRadius: 8,
    padding: 20,
    marginBottom: 24,
  },
  searchResultList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'grid',
    gap: 10,
  },
  searchResultButton: {
    width: '100%',
    display: 'grid',
    gap: 6,
    padding: 14,
    border: '1px solid #d6dee3',
    borderRadius: 8,
    background: '#fbfcfc',
    textAlign: 'left',
    cursor: 'pointer',
  },
};

function Video({ url }: { url: string }) {
  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        paddingTop: '56.25%',
        backgroundColor: '#000',
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      <ReactPlayer
        url={url}
        controls
        width="100%"
        height="100%"
        style={{ position: 'absolute', top: 0, left: 0 }}
      />
    </div>
  );
}

function PublicHelpHeader() {
  const userId = useAppSelector((state) => state.user.details.uid);
  const hasGroups = useAppSelector((state) => state.groups.groups.length > 0);
  const appPath = hasGroups ? '/groups' : '/depts';

  return (
    <Header style={pageStyles.header}>
      <Link to="/" style={pageStyles.brand}>
        <img src={aksLogo} alt="AKS" style={pageStyles.logo} />
        <div style={{ minWidth: 0 }}>
          <Text strong style={{ display: 'block', color: '#0f172a' }}>
            AKS
          </Text>
          <Text type="secondary">Hjælp og FAQ</Text>
        </div>
      </Link>
      <Link to={userId ? appPath : '/sign-in'}>
        <Button icon={<LogIn size={16} />}>{userId ? 'Åbn app' : 'Log ind'}</Button>
      </Link>
    </Header>
  );
}

function GuidedFlowView({ flow, initialNodeId }: { flow: GuidedFlow; initialNodeId?: string }) {
  const resolvedInitialNodeId =
    initialNodeId && flow.nodes[initialNodeId] ? initialNodeId : flow.startNodeId;
  const [currentNodeId, setCurrentNodeId] = useState(resolvedInitialNodeId);
  const [history, setHistory] = useState<string[]>([]);
  const currentNode = getGuidedFlowNode(flow, currentNodeId);
  const isResult = isGuidedFlowResultNode(currentNode);

  useEffect(() => {
    setCurrentNodeId(resolvedInitialNodeId);
    setHistory([]);
  }, [flow.key, resolvedInitialNodeId]);

  const restart = () => {
    setCurrentNodeId(flow.startNodeId);
    setHistory([]);
  };

  const goBack = () => {
    const nextState = goBackGuidedFlow(currentNodeId, history);
    setCurrentNodeId(nextState.currentNodeId);
    setHistory(nextState.history);
  };

  return (
    <section style={pageStyles.flowPanel}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div>
          <Space wrap style={{ marginBottom: 8 }}>
            <Tag color={isResult ? 'success' : 'processing'}>
              {isResult ? 'Resultat' : `Trin ${history.length + 1}`}
            </Tag>
            <Text type="secondary">{flow.title}</Text>
          </Space>
          <Title level={4} style={{ margin: 0 }}>
            {currentNode.title}
          </Title>
          {currentNode.type === 'question' ? (
            <>
              <Paragraph style={{ marginTop: 8, marginBottom: 4 }}>
                {currentNode.question}
              </Paragraph>
              {currentNode.helperText ? (
                <Text type="secondary">{currentNode.helperText}</Text>
              ) : null}
            </>
          ) : (
            <Paragraph style={{ marginTop: 8, marginBottom: 0 }}>{currentNode.summary}</Paragraph>
          )}
        </div>

        {currentNode.type === 'question' ? (
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            {currentNode.answers.map((answer) => (
              <Button
                key={answer.key}
                block
                onClick={() => {
                  const nextState = advanceGuidedFlow(currentNodeId, answer.nextNodeId, history);
                  setCurrentNodeId(nextState.currentNodeId);
                  setHistory(nextState.history);
                }}
                style={pageStyles.answerButton}
              >
                <span style={pageStyles.answerLabel}>{answer.label}</span>
                {answer.description ? (
                  <span style={pageStyles.answerDescription}>{answer.description}</span>
                ) : null}
              </Button>
            ))}
          </Space>
        ) : (
          <div>
            <ul style={pageStyles.resultList}>
              {currentNode.nextSteps.map((step) => (
                <li key={step} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <CheckCircle2
                    size={18}
                    color="#0f766e"
                    style={{ marginTop: 2, flex: '0 0 auto' }}
                  />
                  <Text>{step}</Text>
                </li>
              ))}
            </ul>
            {currentNode.supportText ? (
              <Paragraph style={{ marginBottom: 0 }}>
                <Text strong>Support: </Text>
                {currentNode.supportText}
              </Paragraph>
            ) : null}
          </div>
        )}

        <Space wrap>
          <Button icon={<ArrowLeft size={16} />} disabled={history.length === 0} onClick={goBack}>
            Tilbage
          </Button>
          <Button icon={<RotateCcw size={16} />} onClick={restart}>
            Start forfra
          </Button>
        </Space>
      </Space>
    </section>
  );
}

function TopicOverview({
  topics,
  activeTopicKey,
  onSelectTopic,
}: {
  topics: HelpTopic[];
  activeTopicKey: string;
  onSelectTopic: (topicKey: string) => void;
}) {
  return (
    <div style={pageStyles.topics}>
      {topics.map((topic) => {
        const isActive = topic.key === activeTopicKey;
        return (
          <button
            key={topic.key}
            type="button"
            onClick={() => onSelectTopic(topic.key)}
            style={{
              ...pageStyles.topicButton,
              ...(isActive ? pageStyles.topicButtonActive : {}),
            }}
          >
            <Text strong style={{ display: 'block', marginBottom: 8, color: '#0f172a' }}>
              {topic.title}
            </Text>
            <Text type="secondary">{topic.summary}</Text>
          </button>
        );
      })}
    </div>
  );
}

function ArticleDescription({ lines }: { lines: string[] }) {
  return (
    <Paragraph style={{ marginBottom: 0 }}>
      {lines.map((line, index) => (
        <React.Fragment key={`${line}-${index}`}>
          {line}
          {index < lines.length - 1 ? <br /> : null}
        </React.Fragment>
      ))}
    </Paragraph>
  );
}

function FAQArticles({
  articles,
  activeArticleKey,
  onActiveArticleKeyChange,
}: {
  articles: HelpArticle[];
  activeArticleKey?: string;
  onActiveArticleKeyChange: (articleKey?: string) => void;
}) {
  return (
    <Collapse
      accordion
      activeKey={activeArticleKey}
      onChange={(key) => {
        const nextKey = Array.isArray(key) ? key[0] : key;
        onActiveArticleKeyChange(typeof nextKey === 'string' ? nextKey : undefined);
      }}
      items={articles.map((article) => ({
        key: article.key,
        label: article.title,
        children: (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <ArticleDescription lines={article.description} />
            {article.videoUrl ? <Video url={article.videoUrl} /> : null}
          </div>
        ),
      }))}
    />
  );
}

function getSearchResultKindLabel(kind: HelpSearchResultKind) {
  if (kind === 'article') {
    return 'FAQ';
  }

  if (kind === 'guide') {
    return 'Guidet sti';
  }

  return 'Emne';
}

function FAQSearchResults({
  results,
  onSelectResult,
}: {
  results: HelpSearchResult[];
  onSelectResult: (result: HelpSearchResult) => void;
}) {
  if (results.length === 0) {
    return null;
  }

  return (
    <section style={pageStyles.searchResults}>
      <Title level={3} style={{ marginTop: 0 }}>
        Relaterede svar
      </Title>
      <ul style={pageStyles.searchResultList}>
        {results.map((result) => (
          <li key={result.id}>
            <button
              type="button"
              style={pageStyles.searchResultButton}
              onClick={() => onSelectResult(result)}
            >
              <Space wrap>
                <Tag>{getSearchResultKindLabel(result.kind)}</Tag>
                <Text type="secondary">{result.topicTitle}</Text>
                {result.flowTitle && result.flowTitle !== result.title ? (
                  <Text type="secondary">{result.flowTitle}</Text>
                ) : null}
              </Space>
              <Text strong style={{ color: '#0f172a' }}>
                {result.title}
              </Text>
              <Text type="secondary">{result.summary}</Text>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function getDefaultAppFAQTopicKey(topics: HelpTopic[]) {
  return (
    topics.find((topic) => topic.key === 'tasks')?.key ||
    topics.find((topic) => topic.title === 'Opgaver')?.key ||
    topics[0]?.key ||
    ''
  );
}

function AppFAQContent({
  contentState,
  isLoading,
  navigation,
}: {
  contentState: HelpCenterContentState;
  isLoading: boolean;
  navigation?: React.ReactNode;
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTopicKey, setActiveTopicKey] = useState(() =>
    getDefaultAppFAQTopicKey(contentState.topics)
  );
  const [activeArticleKey, setActiveArticleKey] = useState<string | undefined>(undefined);
  const [activeFlowTarget, setActiveFlowTarget] = useState<
    { flowKey: string; nodeId?: string } | undefined
  >(undefined);

  useEffect(() => {
    if (contentState.topics.some((topic) => topic.key === activeTopicKey)) {
      return;
    }

    setActiveTopicKey(getDefaultAppFAQTopicKey(contentState.topics));
    setActiveArticleKey(undefined);
    setActiveFlowTarget(undefined);
  }, [activeTopicKey, contentState.topics]);

  const searchResults = useMemo(
    () => searchHelpTopics(contentState.topics, searchTerm),
    [contentState.topics, searchTerm]
  );
  const hasSearchTerm = searchTerm.trim().length > 0;
  const searchResultTopicKeys = useMemo(
    () => new Set(searchResults.map((result) => result.topicKey)),
    [searchResults]
  );
  const visibleTopics =
    hasSearchTerm && searchResults.length > 0
      ? contentState.topics.filter((topic) => searchResultTopicKeys.has(topic.key))
      : contentState.topics;
  const activeTopic =
    visibleTopics.find((topic) => topic.key === activeTopicKey) ||
    visibleTopics[0] ||
    contentState.topics[0];
  const hasGuidedFlows = Boolean(activeTopic.guidedFlows && activeTopic.guidedFlows.length > 0);
  const hasArticles = Boolean(activeTopic.articles && activeTopic.articles.length > 0);

  return (
    <>
      <section style={pageStyles.hero}>
        {navigation}
        <div>
          <Title level={1} style={{ marginBottom: 8 }}>
            App FAQ
          </Title>
          <Paragraph style={{ maxWidth: 720, fontSize: 16 }}>
            Find korte svar om appen eller brug en guidet sti, hvor du klikker dig frem til de
            næste relevante trin.
          </Paragraph>
          {isLoading ? <Text type="secondary">Indlæser opdateret indhold...</Text> : null}
        </div>
        <Input
          size="large"
          allowClear
          prefix={<Search size={18} />}
          placeholder="Søg efter fx sensor, opgave, årshjul..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          style={pageStyles.search}
        />
      </section>

      {hasSearchTerm && searchResults.length === 0 ? (
        <Paragraph>
          Ingen emner matcher din søgning endnu. Prøv et andet søgeord eller vælg et emne
          herunder.
        </Paragraph>
      ) : null}

      <TopicOverview
        topics={visibleTopics}
        activeTopicKey={activeTopic.key}
        onSelectTopic={(topicKey) => {
          setActiveTopicKey(topicKey);
          setActiveArticleKey(undefined);
          setActiveFlowTarget(undefined);
        }}
      />

      <section style={pageStyles.topicPanel}>
        <Title level={2} style={{ marginTop: 0 }}>
          {activeTopic.title}
        </Title>
        <Paragraph>{activeTopic.summary}</Paragraph>

        {hasGuidedFlows ? (
          <>
            <Divider orientation="left">Guidede stier</Divider>
            {activeTopic.guidedFlows?.map((flow) => (
              <GuidedFlowView
                key={flow.key}
                flow={flow}
                initialNodeId={
                  activeFlowTarget?.flowKey === flow.key ? activeFlowTarget.nodeId : undefined
                }
              />
            ))}
          </>
        ) : null}

        {hasArticles ? (
          <>
            <Divider orientation="left">FAQ</Divider>
            <FAQArticles
              articles={activeTopic.articles || []}
              activeArticleKey={activeArticleKey}
              onActiveArticleKeyChange={setActiveArticleKey}
            />
          </>
        ) : null}
      </section>

      <FAQSearchResults
        results={searchResults}
        onSelectResult={(result) => {
          setActiveTopicKey(result.topicKey);
          setActiveArticleKey(result.articleKey);
          setActiveFlowTarget(
            result.flowKey
              ? {
                  flowKey: result.flowKey,
                  nodeId: result.flowNodeId,
                }
              : undefined
          );
        }}
      />
    </>
  );
}

export default function FAQ() {
  const [activeFaqView, setActiveFaqView] = useState<FAQView>('documents');
  const [isContentBankDocumentOpen, setIsContentBankDocumentOpen] = useState(false);
  const [contentBankState, setContentBankState] = useState<{
    documents: ContentBankDocument[];
    isLoading: boolean;
    error: string | null;
  }>({
    documents: [],
    isLoading: true,
    error: null,
  });
  const [contentState, setContentState] = useState<HelpCenterContentState>(() => ({
    ...createFallbackHelpCenterState(),
    isLoading: true,
  }));

  useEffect(() => {
    const unsubscribe = subscribeToPublishedContentBankDocuments(
      (documents) => {
        setContentBankState({
          documents,
          isLoading: false,
          error: null,
        });
      },
      (error) => {
        setContentBankState({
          documents: [],
          isLoading: false,
          error: error instanceof Error ? error.message : 'Publiceret content bank kunne ikke hentes.',
        });
      }
    );

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeToPublishedHelpTopics(
      (publishedTopics) => {
        if (!publishedTopics) {
          setContentState(createFallbackHelpCenterState());
          return;
        }

        setContentState({
          topics: publishedTopics,
          source: 'firestore',
          isLoading: false,
          error: null,
        });
      },
      (error) => {
        setContentState({
          ...createFallbackHelpCenterState(),
          error: error instanceof Error ? error.message : 'Kunne ikke hente publiceret hjælp.',
        });
      }
    );

    return () => {
      unsubscribe();
    };
  }, []);

  const hasContentBankDocuments = contentBankState.documents.length > 0;
  const selectedFaqView: FAQView = hasContentBankDocuments ? activeFaqView : 'app';
  const useWideContent = selectedFaqView === 'documents' && isContentBankDocumentOpen;
  const faqNavigation = hasContentBankDocuments ? (
    <Segmented
      value={selectedFaqView}
      onChange={(value) => setActiveFaqView(value as FAQView)}
      options={[
        { label: 'Hjælpedokumenter', value: 'documents' },
        { label: 'App FAQ', value: 'app' },
      ]}
    />
  ) : null;

  useEffect(() => {
    if (!contentBankState.isLoading && contentBankState.documents.length === 0) {
      setActiveFaqView('app');
    }
  }, [contentBankState.documents.length, contentBankState.isLoading]);

  useEffect(() => {
    if (selectedFaqView !== 'documents') {
      setIsContentBankDocumentOpen(false);
    }
  }, [selectedFaqView]);

  return (
    <Layout style={pageStyles.page}>
      <PublicHelpHeader />
      <Content style={{ ...pageStyles.content, ...(useWideContent ? pageStyles.contentWide : null) }}>
        {selectedFaqView === 'documents' ? (
          <PublicFAQContent
            documents={contentBankState.documents}
            isLoading={contentBankState.isLoading}
            error={contentBankState.error}
            navigation={faqNavigation}
            onDocumentOpenChange={setIsContentBankDocumentOpen}
          />
        ) : (
          <AppFAQContent
            contentState={contentState}
            isLoading={contentState.isLoading || contentBankState.isLoading}
            navigation={faqNavigation}
          />
        )}
      </Content>
      <FooterBar />
    </Layout>
  );
}
